import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer, normalizeSectionId } from '@theme/section-renderer';

/**
 * S1 FIX ROUND 5 (2026-08-05, QA pass 4 item D3 — "structurally inert on
 * /cart"):
 *
 * Round 4 homed the bag button's cart-mutation refresh
 * (`CartUpdateEvent`-driven `sectionRenderer.renderSection(..., 'hydration')`
 * of the header section) inside `CartDrawerComponent`
 * (assets/cart-drawer.js). That component is instantiated from
 * snippets/header-actions.liquid, which gates its own render behind
 * `{% if settings.cart_type == 'drawer' and template.name != 'cart' %}`
 * (header-actions.liquid:102) — so `<cart-drawer-component>` never exists
 * on /cart, the one page the refresh was built for. Live netlog confirmed
 * ZERO header fetches on /cart quantity mutations (QA pass 4, assertions
 * 9/11).
 *
 * Root cause: the bag-button refresh is a HEADER concern (the button lives
 * in sections/header.liquid, unconditionally, on every template regardless
 * of `settings.cart_type` or `template.name`), not a DRAWER concern. This
 * module relocates it to a small always-present mechanism that accompanies
 * the bag button itself, loaded from sections/header.liquid without any
 * `cart_type`/`template.name` gate — so it exists on /cart, on drawer mode,
 * and even if a merchant sets `cart_type` to page-based (no drawer at all),
 * uniformly.
 *
 * SINGLE-HOMED: the equivalent listener + `#getHeaderSectionId` helper were
 * REMOVED from CartDrawerComponent (assets/cart-drawer.js) rather than left
 * in place, because on every non-cart page with `cart_type == 'drawer'`
 * BOTH this module and CartDrawerComponent's own `<script>` are present in
 * the DOM — leaving both listeners live would double-fire
 * `sectionRenderer.renderSection()` on every mutation on those pages. This
 * module is now the single home for the "header bag button reacts to
 * ANY cart mutation" concern; CartDrawerComponent still separately owns the
 * `aria-expanded` self-heal (D1, #installBagButtonObserver) and the
 * `data-hydration-key="bag-button"` in-drawer refresh path (D2, unaffected
 * — that path uses the drawer's own `cart-items-component.updateQuantity()`
 * hydration morph of ITS OWN section id, not this event listener).
 *
 * D1 (aria-expanded MutationObserver) correctly STAYS drawer-homed: it
 * exists only to correct `aria-expanded` after a morph resets it to the
 * server default while the drawer dialog is actually open — and
 * `aria-expanded` can only ever be "true" via CartDrawerComponent#open(),
 * which requires the drawer to exist in the first place. On pages where no
 * drawer exists (this module's whole reason to exist), `aria-expanded`
 * never leaves its server default "false", so a hydration-mode morph of
 * this module's own refresh resetting it to "false" is a no-op, not a
 * regression — nothing to protect.
 */

/**
 * Resolves the header section's real runtime id from the live DOM (a
 * theme-instance-numeric-id + JSON-key compound, e.g.
 * `sections--22242979938528__header_section` — never a hardcoded literal
 * "header"; see assets/cart-drawer.js history for why that assumption was
 * previously wrong). Anchored off the bag button itself (`[data-cart-open]`,
 * sections/header.liquid), which — unlike `<cart-drawer-component>` — is
 * unconditionally rendered on every template.
 *
 * @returns {string | null}
 */
function getHeaderSectionId() {
  const bagBtn = document.querySelector('[data-cart-open]');
  const headerSectionEl = bagBtn?.closest('.shopify-section');
  if (!headerSectionEl?.id) return null;
  return normalizeSectionId(headerSectionEl.id);
}

/**
 * Re-hydrates the header section's keyed bag-button subtree
 * (`data-hydration-key="bag-button"`) after any cart quantity mutation that
 * did NOT originate from the header section itself (in-drawer mutations
 * already refresh the bag button via the drawer's own hydration morph of
 * its own section id — see D2 in assets/cart-drawer.js; re-triggering here
 * would be a redundant second fetch for the same data).
 *
 * `CartAddEvent` shares the same event type string (`ThemeEvents.cartUpdate`)
 * but is a distinct class, not extending `CartUpdateEvent` — the
 * `instanceof` guard below excludes ATC, matching D3's original scope
 * (quantity-mutation paths only).
 *
 * @param {Event} event
 */
function handleHeaderRefresh(event) {
  if (!(event instanceof CartUpdateEvent)) return;
  const headerSectionId = getHeaderSectionId();
  if (!headerSectionId) return;
  if (event.detail?.sourceId === headerSectionId) return;
  sectionRenderer.renderSection(headerSectionId, { cache: false, mode: 'hydration' });
}

document.addEventListener(ThemeEvents.cartUpdate, handleHeaderRefresh);
